import 'js/wdyr';
import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import { createBrowserHistory } from 'history';
import { Redirect, Route, Router, Switch } from 'react-router-dom';

// context
import LeafyGreenProvider from '@leafygreen-ui/leafygreen-provider';
import { RequestParamsProvider, useRequestParams } from 'js/common/context/RequestParamsContext';
import { AccountUserProvider } from 'js/auth/context/AccountUserContext';
import { AppUserProvider } from 'js/auth/context/AppUserContext';

// services
import { initializeAuthClient } from 'js/common/utils/oktaUtils';

// hooks
import useIntercomService from 'js/auth/hooks/useIntercomService';
import useBugsnag from 'js/common/hooks/useBugsnag';
import useAnalytics from 'js/common/hooks/useAnalytics';

// analytics
import { AppEnv, initializeAnalytics } from 'js/project/common/util/segmentAnalytics';
// utils
import { sanitize } from 'js/auth/utils/authRedirectUtils';
import processAuth from 'js/auth/utils/postAuthUtils';

// components
import RegistrationPage from './components/RegistrationPage';
import RegistrationSuccessPage from './components/RegistrationSuccessPage';
import LoginPage from './components/LoginPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import MultiFactorPage from './components/MultiFactorPage';
import AccountMultiFactorAuthPage from './components/AccountMultiFactorAuthPage';
import ResetMultiFactorPage from './components/ResetMultiFactorPage';
import MultiFactorSetupPage from './components/MultiFactorSetupPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import UniversityPasswordResetPage from './components/UniversityPasswordResetPage';
import IdentityVerificationPage from './components/IdentityVerificationPage';
import EmailVerificationPage from './components/EmailVerificationPage';
import VercelConfirmationPage from './components/VercelConfirmationPage';
import VercelClusterLinkingPage from './components/VercelClusterLinkingPage';
import VercelClusterCreationPage from './components/VercelClusterCreationPage';
import VercelOrgSelectionPage from './components/VercelOrgSelectionPage';
import { DeviceAuthPage } from './components/DeviceAuthPage';
import { DeviceAuthSuccessPage } from './components/DeviceAuthSuccessPage';

const browserHistory = createBrowserHistory({
  basename: 'account',
});
const { pathname, hash } = browserHistory.location;

/* we should get rid of url fragments (aka hashes) in the urls
  that could possibly be present from the /user#/(atlas|cloud|ops)/login redirect
  but preserve the hash values after the plan type to correctly redirect to their new locations
*/
if (hash) {
  const sanitizedLocation = sanitize(browserHistory.location);

  browserHistory.replace({
    pathname: sanitizedLocation.pathname,
    search: sanitizedLocation.search,
    hash: sanitizedLocation.hash,
  });
}

const {
  appEnv = AppEnv.PROD,
  onPrem,
  oktaAuthRedirectBase,
  oktaBaseUrl,
  oktaClientId,
  analyticsEnabled,
  segmentClientSideWriteKey,
  accountMultiFactorAuthEnabled,
  accountCentralUrl,
} = window.REQUEST_PARAMS;

// the Okta auth client needs to be initialized before the app is rendered so we can't move this into `useEffect`
if (!onPrem) {
  initializeAuthClient({
    oktaRedirectBase: oktaAuthRedirectBase,
    oktaUrl: oktaBaseUrl,
    cloudClientId: oktaClientId,
  });
}

const getShouldRequestAuid = (): boolean => {
  // we are only initializing analytics on the registration success page
  // and not on other pages of the auth app
  const pathName = window.location.pathname;
  return pathName === '/account/register/success';
};

initializeAnalytics({
  analyticsEnabled,
  segmentId: segmentClientSideWriteKey,
  appId: 'auth',
  appEnv,
  accountCentralUrl,
  shouldRequestAuid: getShouldRequestAuid(),
});

function App() {
  const requestParams = useRequestParams();

  useIntercomService(browserHistory);
  useBugsnag('Registration', requestParams);
  useAnalytics(browserHistory);

  if (pathname === '/oauth') {
    // handles all the post auth logic from oidc/callback return
    processAuth({ requestParams, location: browserHistory.location });
    return null;
  }

  return (
    <RequestParamsProvider>
      <AccountUserProvider>
        <AppUserProvider>
          <Router history={browserHistory}>
            <Switch>
              <Route path="/login/mfa" component={MultiFactorPage} exact />
              <Route path="/login/:integration?" component={LoginPage} exact />
              <Route path="/register" component={RegistrationPage} exact />
              <Route path="/register/success" component={RegistrationSuccessPage} exact />
              <Route path="/register/:integration" component={RegistrationPage} exact />
              <Route path="/vercel/confirm" component={VercelConfirmationPage} exact />
              <Route path="/vercel/cluster/link" component={VercelClusterLinkingPage} exact />
              <Route path="/vercel/cluster/create" component={VercelClusterCreationPage} exact />
              <Route path="/vercel/org/selection" component={VercelOrgSelectionPage} exact />
              <Route path="/tos" component={TermsOfServicePage} />
              <Route path="/reset/password/:tempId?" component={ResetPasswordPage} />
              <Route path="/reset/university/password" component={UniversityPasswordResetPage} exact />
              <Route path="/connect" component={DeviceAuthPage} exact />
              <Route path="/connect/success" component={DeviceAuthSuccessPage} exact />
              <Route path="/reset/mfa/:planType/:tempId?" component={ResetMultiFactorPage} exact />
              <Route path="/mfa/setup" component={MultiFactorSetupPage} />
              <Route path="/auth/verify" component={IdentityVerificationPage} />
              <Route path="/email/verify" component={EmailVerificationPage} />
              {accountMultiFactorAuthEnabled && (
                <Route path="/security/mfa" component={AccountMultiFactorAuthPage} exact />
              )}
              <Route
                path="/"
                component={({ location }) => (
                  <Redirect
                    to={{
                      ...location,
                      pathname: '/login',
                    }}
                  />
                )}
              />
            </Switch>
          </Router>
        </AppUserProvider>
      </AccountUserProvider>
    </RequestParamsProvider>
  );
}

ReactDOM.render(
  <StrictMode>
    <LeafyGreenProvider>
      <App />
    </LeafyGreenProvider>
  </StrictMode>,
  document.getElementById('mms-body-application')
);
